import type { TransportOptions } from './transport'

/** 读取 TURN 临时凭证的超时时间（毫秒）。 */
const TURN_FETCH_TIMEOUT_MS = 3000

/**
 * 公共兜底 ICE（STUN + 公共 TURN）。
 *
 * ⚠️ 安全要求：这里**只允许**公开共享的服务器。
 * 旧版 web/index.html 里硬编码的自建 TURN（turn:neptunes.top）账号密码已经泄露，
 * 严禁在本包中重新引入；自建 TURN 由部署方通过 TransportOptions.extraIceServers 注入。
 */
export const STATIC_ICE_SERVERS: readonly RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  {
    // PeerJS 官方的公共 TURN，仅作兜底
    urls: ['turn:eu-0.turn.peerjs.com:3478', 'turn:us-0.turn.peerjs.com:3478'],
    username: 'peerjs',
    credential: 'peerjsp',
  },
  {
    // OpenRelay 提供的公共 TURN，多个端口/传输方式提高穿透成功率
    urls: 'turn:openrelay.metered.ca:80',
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
  {
    urls: 'turn:openrelay.metered.ca:443',
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
  {
    urls: 'turn:openrelay.metered.ca:443?transport=tcp',
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
]

function iceUrls(server: RTCIceServer): string[] {
  return Array.isArray(server.urls) ? [...server.urls] : [server.urls]
}

function iceServerKey(server: RTCIceServer): string {
  // urls 排序后再比较，避免 ['a','b'] 与 ['b','a'] 被当成两个不同的服务器
  const urls = iceUrls(server).sort().join('|')
  return `${urls}::${server.username ?? ''}::${server.credential ?? ''}`
}

/**
 * 合并 ICE 服务器：临时凭证 → 部署方自建 → 公共兜底，并按「同一台服务器」去重（先出现的优先）。
 */
export function mergeIceServers(
  fetched: readonly RTCIceServer[],
  extra?: readonly RTCIceServer[],
): RTCIceServer[] {
  const merged: RTCIceServer[] = []
  const seen = new Set<string>()
  for (const server of [...fetched, ...(extra ?? []), ...STATIC_ICE_SERVERS]) {
    const key = iceServerKey(server)
    if (seen.has(key)) continue
    seen.add(key)
    merged.push(server)
  }
  return merged
}

/** 校验并规范化上游返回的 iceServers；任何不合法条目都被丢弃。 */
function parseIceServers(payload: unknown): RTCIceServer[] {
  if (typeof payload !== 'object' || payload === null) return []
  const raw = (payload as { iceServers?: unknown }).iceServers
  if (!Array.isArray(raw)) return []

  const servers: RTCIceServer[] = []
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue
    const record = entry as { urls?: unknown; username?: unknown; credential?: unknown }

    let urls: string | string[] | null = null
    if (typeof record.urls === 'string' && record.urls.length > 0) {
      urls = record.urls
    } else if (
      Array.isArray(record.urls) &&
      record.urls.length > 0 &&
      record.urls.every((url): url is string => typeof url === 'string')
    ) {
      urls = [...record.urls]
    }
    if (urls === null) continue

    const server: RTCIceServer = { urls }
    if (typeof record.username === 'string') server.username = record.username
    if (typeof record.credential === 'string') server.credential = record.credential
    servers.push(server)
  }
  return servers
}

/**
 * 读取 Worker 签发的临时 TURN 凭证。
 *
 * 失败 / 非 2xx / 超时（3s）/ 返回体格式错误一律返回 []，绝不抛异常——
 * 取不到临时凭证时应当静默回退到 STATIC_ICE_SERVERS，不能拖垮建连流程。
 */
export async function fetchTurnCredentials(
  endpoint: string,
  fetchImpl?: typeof fetch,
): Promise<RTCIceServer[]> {
  const request = fetchImpl ?? globalThis.fetch
  if (typeof request !== 'function') return []

  const controller = typeof AbortController === 'undefined' ? null : new AbortController()
  const timer =
    controller === null ? null : setTimeout(() => controller.abort(), TURN_FETCH_TIMEOUT_MS)

  try {
    const response = await request(endpoint, controller === null ? {} : { signal: controller.signal })
    if (!response.ok) return []
    return parseIceServers((await response.json()) as unknown)
  } catch {
    // 网络错误 / 超时 / 非法 JSON 都属于「取不到凭证」，静默回退
    return []
  } finally {
    if (timer !== null) clearTimeout(timer)
  }
}

/** 组装 PeerJS 选项（传入自定义 config 后 PeerJS 不会再叠加它自带的默认 ICE 列表）。 */
export async function buildPeerOptions(
  options?: TransportOptions,
): Promise<{ config: { iceServers: RTCIceServer[] } }> {
  const endpoint = options?.turnEndpoint ?? '/turn'
  const fetched = await fetchTurnCredentials(endpoint)
  return { config: { iceServers: mergeIceServers(fetched, options?.extraIceServers) } }
}
